import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import { debounce } from 'lodash';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import ArrowForwardIos from '@material-ui/icons/ArrowForwardIos';
import ArrowBackIos from '@material-ui/icons/ArrowBackIos';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { MTableToolbar } from '@material-table/core';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import DoneIcon from '@material-ui/icons/Done';
import AddIcon from '@material-ui/icons/Add';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { fractionToPercent, searchInStrings, formatNumber } from 'helpers/helpers';
import Info from 'components/Tooltip/Info';
import SteppedBar from 'components/Bars/SteppedBar';
import RangeBar from 'components/Bars/RangeBar';
import PieGraph from 'components/Graph/PieGraph';
import PortfolioTableCurrent from 'components/PortfolioTables/PortfolioTableCurrent';
import MaterialTable from 'components/Table/Table';
import AssetDetail from 'components/PortfolioTables/AssetDetail';
import LoadingIndicator from 'components/Loading/LoadingIndicator';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import FilterMenu from 'components/FilterMenu/FilterMenu';

import { AssetCategory } from 'common/interfaces';
import useStyles from './PortfolioEditorStyles';
import { WorkflowPlanStore } from 'stores/workflowStores/newWorkflowPlanStore/workflowPlanStore';
import { PortfolioEditorStore } from 'stores/workflowStores/newWorkflowPlanStore/portfolioEditorStore';
import { RootStore } from 'stores/rootStore';
import colors from 'components/PortfolioTables/colors';
import useOtherStyles from 'components/PortfolioTables/otherStyles';
import { BucketType } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import { Currency } from 'enums';
import { AssetChartStore } from 'stores/workflowStores/newWorkflowPlanStore/assetChartStore';

interface Props {
  isOpen: boolean;
  planStore: WorkflowPlanStore;
  rootStore?: RootStore;
  accountId: string;
  bucket: BucketType;
  handleOpen: (open: boolean) => void;
  detailMode: boolean;
  currency: Currency;
  rebalancing?: boolean;
  originalPortfolioCode?: string;
}

const categories = [
  { value: 'IK1', label: 'IK1', color: colors['IK1'] },
  { value: 'IK2', label: 'IK2', color: colors['IK2'] },
  { value: 'IK3', label: 'IK3', color: colors['IK3'] },
  { value: 'IK4', label: 'IK4', color: colors['IK4'] },
];

const PortfolioEditor: React.FC<Props> = ({
  isOpen,
  planStore,
  rootStore,
  accountId,
  handleOpen,
  bucket,
  detailMode,
  currency,
  rebalancing,
  originalPortfolioCode,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const otherClasses = useOtherStyles();

  const [store] = useState(new PortfolioEditorStore(rootStore as RootStore, accountId, detailMode, bucket));
  const [chartStore] = useState(new AssetChartStore(rootStore!));

  const [selectedCategory, setSelectedCategory] = useState('IK1');
  const [nameEditing, setNameEditing] = useState(false);
  const defaultName = t('IND');
  const [name, setName] = useState(defaultName);
  const [oneTimeRebalanceOpen, setOneTimeRebalanceOpen] = useState(false);

  const laptop = useMediaQuery('@media (max-width: 1279px)', { noSsr: true });
  const tablet = useMediaQuery('@media (max-width: 1023px)', { noSsr: true });

  const tableRef = React.createRef<any>();

  const process = useMemo(
    () =>
      debounce(async () => {
        await store.process();
      }, 500),
    [store],
  );

  const check = useMemo(
    () => async (categories: AssetCategory[]) => {
      store.check(categories);
    },
    [store],
  );

  useEffect(() => {
    (async () => {
      await store.fetchData();
    })();
  }, [store]);

  useEffect(() => {
    if (store.data?.title) {
      setName(store.data.title);
      store.setTitle(store.data.title);
    } else {
      store.setTitle(defaultName);
    }
  }, [store, store.data?.title, defaultName]);

  const save = async () => {
    const success = await store.save();
    if (success) {
      handleOpen(false);
      planStore.setPortfolioCode('IND,IND', bucket);
    }
  };

  const checkForChange = async () => {
    if (
      originalPortfolioCode !== 'IND' ||
      !store.fingerprintsMatch() ||
      store.data?.calculatedProfile !== store.data?.bucketProfile
    ) {
      setOneTimeRebalanceOpen(true);
    } else {
      await save();
    }
  };

  return (
    <>
      <Dialog open={isOpen} fullWidth maxWidth="xl">
        <DialogHeader />
        <DialogContent className={classes.dialog}>
          {store.data && store.sum ? (
            <Grid container spacing={4} className={classes.gridFix}>
              <Grid container item xs={6} className={classes.section}>
                {nameEditing ? (
                  <>
                    <Grid container item xs={10} className={classes.title}>
                      <TextField value={name} onChange={(e) => setName(e.target.value)} />
                    </Grid>
                    <Grid container item xs={2} justifyContent="center" className={classes.title}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          setNameEditing(false);
                          store.setTitle(name);
                        }}
                      >
                        <DoneIcon />
                      </Button>
                    </Grid>
                  </>
                ) : (
                  <Grid container item xs={12} className={classes.title}>
                    <Typography variant={'h2'}>{name}</Typography>
                    <IconButton size="small" onClick={() => setNameEditing(true)}>
                      <EditOutlinedIcon />
                    </IconButton>
                  </Grid>
                )}
                <div style={{ opacity: store.editing || store.processing ? 0.25 : 1 }} className={classes.pie}>
                  <PieGraph
                    data={store.pie}
                    title={name}
                    settings={{
                      chart: { height: 320 },
                      legend: { enabled: false },
                    }}
                  />
                </div>
                {store.selectedAssets && (
                  <PortfolioTableCurrent
                    store={store}
                    editor
                    detailMode={false}
                    assetCategories={store.selectedAssets}
                    handleProcessing={process}
                    handleChecking={check}
                    processing={store.processing}
                    reservedCashCode={store.data.reservedCashCode}
                    savingIsins={planStore.data?.options.specialAssets.savings}
                  />
                )}
              </Grid>
              <Grid container item xs={6} spacing={4} className={`${classes.section}`}>
                <Grid container item spacing={0} className={`${classes.wrapper} sectionBuffer`}>
                  <Grid item xs={12}>
                    <Typography variant={'h4'}>
                      {t('general_portfolioStrategy')}
                      <Info
                        content={
                          <span
                            dangerouslySetInnerHTML={{
                              __html: t('portfolio_editor_riskCategory_tooltip', {
                                interpolation: { escapeValue: false },
                              }),
                            }}
                          />
                        }
                        heading
                      />
                    </Typography>
                    <SteppedBar
                      currentValue={store.data.calculatedProfile}
                      targetValue={store.data.bucketProfile}
                      min={store.data.minProfile}
                      max={store.data.maxProfile}
                      className="riskAlt"
                      style={{ opacity: store.editing || store.processing ? 0.25 : 1 }}
                    />
                  </Grid>
                  <Grid item xs={6} style={{ paddingRight: '32px' }}>
                    <Typography variant={'h4'}>
                      {t('esg_rating')} <Info content={t('portfolio_editor_esg_tooltip')} heading />
                    </Typography>
                    <RangeBar
                      min={1}
                      max={10}
                      currentValue={store.data.esgScore}
                      style={{ opacity: store.editing || store.processing ? 0.25 : 1 }}
                    />
                  </Grid>
                  <Grid item xs={6} className={classes.ter}>
                    <Typography variant={'h4'}>
                      {t('portfolio_editor_ter')} <Info content={t('portfolio_editor_ter_tooltip')} heading />
                    </Typography>
                    <RangeBar
                      min={0}
                      max={0.03}
                      currentValue={store.data.terAverage}
                      percentage
                      style={{ opacity: store.editing || store.processing ? 0.25 : 1 }}
                    />
                  </Grid>
                  {store.processing && <LoadingIndicator size={40} />}
                </Grid>

                <Grid item xs={12}>
                  <Typography variant={'h4'}>{t('portfolio_editor_instruments_title')}</Typography>

                  <MaterialTable
                    tableRef={tableRef}
                    className={classes.table}
                    options={{
                      draggable: false,
                      pageSize: 20,
                      rowStyle: (rowData, index, level) => {
                        return { color: colors[rowData.assetCategory] };
                      },
                    }}
                    components={{
                      Toolbar: (props) => (
                        <Grid container>
                          <Grid item xs={12}>
                            <MTableToolbar {...props} />
                          </Grid>
                          <Grid item xs={12} className={classes.categories}>
                            <FilterMenu
                              onClick={setSelectedCategory}
                              items={categories}
                              currentValue={selectedCategory}
                              autoSize={false}
                              mobile={'1279'}
                            />
                          </Grid>
                        </Grid>
                      ),
                    }}
                    data={store.filterETFs(selectedCategory)}
                    columns={[
                      {
                        field: 'title',
                        title: t('accountPlan_portfolioTable_asset'),
                        cellStyle: { width: '75%' },
                        customFilterAndSearch: (filter, rowData, def) =>
                          searchInStrings(filter, [
                            rowData.title,
                            rowData.description,
                            rowData.isin,
                            rowData.originalTitle,
                          ]),
                      },
                      {
                        field: 'ter',
                        title: t('accountPlan_portfolioTable_TER'),
                        render: (rowData) => (rowData.ter !== undefined ? fractionToPercent(rowData.ter, 2) : ''),
                        hidden: laptop || tablet,
                      },
                      {
                        field: 'esgScore',
                        title: t('esg_rating'),
                        render: (rowData) => (rowData.esgScore !== undefined ? formatNumber(rowData.esgScore, 2) : ''),
                        hidden: laptop || tablet,
                      },
                      {
                        field: 'risk',
                        title: (
                          <>
                            {t('accountPlan_portfolioTable_risk')}
                            <Info content={t('portfolio_editor_risk_tooltip')} inline />
                          </>
                        ),
                        render: (rowData) => rowData.risk,
                        cellStyle: { textAlign: 'center' },
                      },
                      {
                        field: 'action',
                        title: t('general_add'),

                        render: (asset) => (
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              store.addToSelectedAssets(asset);
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        ),
                      },
                    ]}
                    detailPanel={[
                      {
                        render: ({ rowData }) => (
                          <div className={`${otherClasses.assetDetail} ${otherClasses.assetShown}`}>
                            <AssetDetail
                              asset={rowData}
                              chartStore={chartStore}
                              active={true}
                              savingIsins={planStore.data?.options.specialAssets.savings}
                            />
                          </div>
                        ),
                      },
                    ]}
                    onRowClick={(event, rowData, togglePanel) => {
                      if (tableRef?.current) {
                        tableRef.current.state.data = tableRef.current.state.data!.map((data: any) => {
                          data.tableData.showDetailPanel = false;
                          return data;
                        });
                      }
                      togglePanel!();
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <LoadingIndicator size={64} />
          )}
        </DialogContent>

        <DialogActions className={classes.actions}>
          <div>
            {store.sum && (
              <div className={`${classes.infoSum} ${store.sum !== 100 ? otherClasses.incorrectSum : ''}`}>
                {t('accountPlan_portfolioTable_totalAlt')}:{' '}
                <strong className={store.sum === 100 ? otherClasses.correctSum : otherClasses.incorrectSum}>
                  {store.sum}
                  {' '}%
                </strong>
              </div>
            )}
            {store.assetUnderLimit && (
              <strong className={otherClasses.incorrectSum}>{t('accountPlan_portfolioTable_assetUnderLimit')}</strong>
            )}
            {store.assetOverLimit && (
              <strong className={otherClasses.incorrectSum}>{t('accountPlan_portfolioTable_assetOverLimit')}</strong>
            )}
          </div>
          <div>
            <Button onClick={() => handleOpen(false)} color="default" variant="contained" startIcon={<ArrowBackIos />}>
              {t('general_cancel')}
            </Button>
            <Button
              onClick={detailMode ? checkForChange : save}
              color="primary"
              variant="contained"
              endIcon={<ArrowForwardIos />}
              disabled={store.editing || store.processing || !store.valid || !store.profileValid}
            >
              {t('general_save')}
            </Button>
          </div>
          <div />
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        disableClose
        title={`accountDetail_plan_oneTimeRebalancing_title_${rebalancing === false ? 'off' : 'on'}`}
        btnCloseText="accountDetail_plan_oneTimeRebalancing_dont"
        btnConfirmText="accountDetail_plan_oneTimeRebalancing_do"
        isOpen={oneTimeRebalanceOpen}
        close={async () => {
          planStore && (await planStore.setOneTimeRebalancing(bucket, false));
          setOneTimeRebalanceOpen(false);
          await save();
        }}
        confirm={async () => {
          planStore && (await planStore.setOneTimeRebalancing(bucket, true));
          setOneTimeRebalanceOpen(false);
          await save();
        }}
      >
        <p>{t(`accountDetail_plan_oneTimeRebalancing_auto${rebalancing === false ? 'Off' : 'On'}_info`)}</p>
        <p>{t(`accountDetail_plan_oneTimeRebalancing_auto${rebalancing === false ? 'Off' : 'On'}_info2`)}</p>
      </ConfirmDialog>
    </>
  );
};

export default inject('rootStore')(observer(PortfolioEditor));
